import { supabase } from "@/integrations/supabase/client";
import type { Database, Tables } from "@/integrations/supabase/types";
import type { RealtimeChannel } from "@supabase/supabase-js";

type MessageInsert = Database["public"]["Tables"]["messages"]["Insert"];

export async function getMessagesByCollection(collectionId: string) {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .eq("collection_id", collectionId)
    .order("created_at", { ascending: false });
  return { data: data || [], error };
}

export async function getMessageCount(collectionId: string) {
  const { count, error } = await supabase
    .from("messages")
    .select("*", { count: "exact", head: true })
    .eq("collection_id", collectionId);
  return { count: count || 0, error };
}

export async function getMessageCountsForCollections(collectionIds: string[]) {
  if (collectionIds.length === 0) return new Map<string, number>();

  const { data, error } = await supabase
    .from("messages")
    .select("collection_id, contributor_email, contributor_name")
    .in("collection_id", collectionIds);

  if (error || !data) return new Map<string, number>();

  const uniqueSigners = new Map<string, Set<string>>();
  for (const id of collectionIds) uniqueSigners.set(id, new Set());
  for (const row of data) {
    const key = row.contributor_email || row.contributor_name;
    uniqueSigners.get(row.collection_id)?.add(key);
  }

  const counts = new Map<string, number>();
  for (const [id, signers] of uniqueSigners) {
    counts.set(id, signers.size);
  }
  return counts;
}

export async function createMessage(message: MessageInsert) {
  const { data, error } = await supabase
    .from("messages")
    .insert(message);
  return { data, error };
}

export function subscribeToMessages(
  collectionId: string,
  onInsert: (message: Tables<"messages">) => void,
): RealtimeChannel {
  return supabase
    .channel(`card-${collectionId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `collection_id=eq.${collectionId}`,
      },
      (payload) => onInsert(payload.new as Tables<"messages">),
    )
    .subscribe((_status, err) => {
      if (err) {
        console.error("Realtime subscription error:", err.message);
      }
    });
}

export function unsubscribeFromMessages(channel: RealtimeChannel) {
  supabase.removeChannel(channel);
}
